import styled from "styled-components";

import { FlexColumn } from "@similarweb/ui-components";
import { Stack } from "@similarweb/ui-components/foundations";

import { WidgetContainer } from "app/components/React/WidgetContainer/WidgetContainer";
import { useSubtitleFilters } from "app/custom-hooks/useSubtitleFilters";
import { translate } from "app/filters/i18nFilter";
import { AdIntelMetricsUnavailableBanner } from "app/pages/digital-marketing/ad-intelligence/common/banners/AdIntelMetricsUnavailableBanner";
import { useDisplayAdvertiserPublisherDataAndParams } from "app/pages/digital-marketing/ad-intelligence/common/hooks/useDisplayAdvertiserPublisherDataAndParams";
import type { AdIntelligenceController } from "app/pages/digital-marketing/ad-intelligence/common/shared/types/types";
import DurationService from "app/services/DurationService";

import { DisplayAdvertisingPerformanceBottomWidget } from "./DisplayAdvertisingPerformanceBottomWidget";
import { useActivityBreakdownData } from "./hooks/useActivityBreakdownData";
import { useAdPlacements } from "./hooks/useAdPlacements";
import { usePerformanceQuery } from "./hooks/usePerformanceQuery";
import { PerformanceEntitiesWidget } from "./PerformanceEntitiesWidget";
import { PerformanceOverviewWidget } from "./PerformanceOverviewWidget";
import { Divider, PerformanceEntitiesWidgetContainer, PerformanceWidgetContainer } from "./performanceShared";
import { ShortEmptyState } from "./shortEmptyState";
import type { EntitiesMetric, PerformanceMetric, PerformanceResponse } from "./types";
import { PlacementKeys } from "./types";
import { fillMissingPlacementKeys } from "./utils/graphUtils";

export { defaultMinHeight, getEmptyState } from "./performanceShared";

const PerformanceWidgetsWrapper = styled(Stack)`
    gap: 24px;
    margin-bottom: 24px;
    align-items: stretch;
`;

const BannerAndPerformanceContainer = styled(FlexColumn)`
    height: 100%;
`;

const isEmptyState = (
    data: PerformanceResponse | Record<string, unknown> | undefined,
    testProperty: keyof PerformanceMetric | keyof EntitiesMetric,
) => {
    if (!data) {
        return true;
    }
    return (
        Object.keys(data).length === 0 ||
        Object.keys(data).every((key) => !(data[key] as Record<string, unknown>)?.[testProperty])
    );
};

interface IPerformanceWidget {
    controller?: AdIntelligenceController;
    performanceOverTimeGraphComponent?: React.ReactNode;
}

export const PerformanceWidget = ({
    controller = "advertiser",
    performanceOverTimeGraphComponent,
}: IPerformanceWidget) => {
    const subtitleFilters = useSubtitleFilters("date", "country", "webSource");
    const combinedSubtitleFilters = subtitleFilters;
    const performanceQuery = usePerformanceQuery(controller);
    const placementsQuery = useAdPlacements(controller);
    const {
        data: entitiesData,
        error: entitiesError,
        isLoading: entitiesLoading,
    } = useActivityBreakdownData(controller);
    const params = useDisplayAdvertiserPublisherDataAndParams();
    const { duration } = params;
    const { isWindow } = DurationService.getDurationData(duration).forAPI;

    const transformedPlacementsData = placementsQuery.data?.data?.[0]?.placementPercentages
        ? (() => {
              const filledPlacement = fillMissingPlacementKeys(placementsQuery.data.data[0].placementPercentages);
              return {
                  above: filledPlacement[PlacementKeys.ABOVE_THE_FOLD],
                  below: filledPlacement[PlacementKeys.BELOW_THE_FOLD],
                  farBelow: filledPlacement[PlacementKeys.FAR_BELOW_THE_FOLD],
              };
          })()
        : undefined;

    const singleDomainData = performanceQuery.data?.records
        ? Object.values(performanceQuery.data.records)[0]
        : undefined;

    const performanceData: PerformanceResponse | undefined = singleDomainData
        ? {
              ...singleDomainData,
              placements: transformedPlacementsData || singleDomainData.placements,
              startDate: entitiesData?.startDate,
              hasAdIntelMetrics: performanceQuery.data?.hasAdIntelMetrics,
          }
        : undefined;

    const performanceLoading = performanceQuery.isPending || placementsQuery.isLoading || placementsQuery.isFetching;
    const performanceError = performanceQuery.isError || placementsQuery.isError;

    const showPerformanceEmptyState = !performanceLoading && isEmptyState(performanceData, "value");
    const showBreakdownEmptyState = !performanceLoading && isEmptyState(performanceData, "desktopShare");
    const showPlacementsEmptyState =
        !performanceLoading && Object.values(performanceData?.placements ?? {}).every((value) => !value);
    const showEntitiesEmptyState =
        !entitiesLoading && isEmptyState(entitiesData as unknown as Record<string, unknown>, "count");

    return (
        <PerformanceWidgetsWrapper>
            <WidgetContainer
                title={translate(`ad.intelligence.${controller}.overview.performance.widget.title`)}
                tooltip={translate(`ad.intelligence.${controller}.overview.performance.widget.title.tooltip`)}
                subtitleFilters={combinedSubtitleFilters}
                withBorder
            >
                <BannerAndPerformanceContainer>
                    {performanceData?.hasAdIntelMetrics === false && !showPerformanceEmptyState ? (
                        <AdIntelMetricsUnavailableBanner />
                    ) : null}
                    <PerformanceWidgetContainer>
                        {showPerformanceEmptyState ? (
                            <ShortEmptyState
                                isError={performanceError}
                                minHeight={98}
                            />
                        ) : (
                            <PerformanceOverviewWidget
                                data={performanceData}
                                isLoading={performanceLoading}
                                controller={controller}
                                isWindow={isWindow}
                                withUpdatedBadge
                            />
                        )}
                        <Divider />
                        <PerformanceEntitiesWidgetContainer>
                            {showEntitiesEmptyState ? (
                                <ShortEmptyState
                                    isError={entitiesError}
                                    minHeight={98}
                                />
                            ) : (
                                <PerformanceEntitiesWidget
                                    data={entitiesData}
                                    isLoading={entitiesLoading}
                                    controller={controller}
                                    isWindow={isWindow}
                                />
                            )}
                        </PerformanceEntitiesWidgetContainer>
                    </PerformanceWidgetContainer>
                </BannerAndPerformanceContainer>
            </WidgetContainer>
            {performanceOverTimeGraphComponent}
            {controller === "publisher" ? null : (
                <DisplayAdvertisingPerformanceBottomWidget
                    isLoading={performanceLoading}
                    spendShare={performanceData?.spend?.desktopShare}
                    impressionsShare={performanceData?.impressions?.desktopShare}
                    placement={performanceData?.placements}
                    startDate={performanceData?.startDate}
                    visitsShare={performanceData?.visits?.desktopShare}
                    showBreakdownEmptyState={showBreakdownEmptyState}
                    showPlacementsEmptyState={showPlacementsEmptyState}
                    performanceError={performanceError}
                    controller={controller}
                    hasAdIntelMetrics={performanceData?.hasAdIntelMetrics}
                />
            )}
        </PerformanceWidgetsWrapper>
    );
};
